import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Search, X } from 'lucide-react'
import { useLaboratorios } from '../hooks/usePricing'
import type { CotizacionFilters } from '../types'

interface PricingFiltersProps {
  filters: CotizacionFilters
  onChange: (filters: CotizacionFilters) => void
}

function generarUltimos12Meses(): { value: string; label: string }[] {
  const meses: { value: string; label: string }[] = []
  const hoy = new Date()
  for (let i = 0; i < 12; i++) {
    const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1)
    const value = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`
    const label = fecha.toLocaleDateString('es-EC', { year: 'numeric', month: 'long' })
    meses.push({ value, label })
  }
  return meses
}

export function PricingFilters({ filters, onChange }: PricingFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search || '')
  const { data: laboratorios } = useLaboratorios()
  const meses = generarUltimos12Meses()

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== (filters.search || '')) {
        onChange({ ...filters, search: searchInput || undefined, page: 1 })
      }
    }, 400)
    return () => clearTimeout(timeout)
  }, [searchInput])

  const tieneFilrosActivos = filters.mes || filters.laboratorio || filters.estado || filters.search

  const limpiarFiltros = () => {
    setSearchInput('')
    onChange({ page: 1, page_size: filters.page_size })
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, email..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9"
        />
      </div>

      <Select
        value={filters.mes || 'todos'}
        onValueChange={(v) => onChange({ ...filters, mes: v === 'todos' ? undefined : v, page: 1 })}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Mes" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="todos">Todos los meses</SelectItem>
          {meses.map((m) => (
            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.laboratorio || 'todos'}
        onValueChange={(v) => onChange({ ...filters, laboratorio: v === 'todos' ? undefined : v, page: 1 })}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Laboratorio" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="todos">Todos los labs</SelectItem>
          {laboratorios?.map((lab) => (
            <SelectItem key={lab} value={lab}>{lab}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.estado || 'todos'}
        onValueChange={(v) => onChange({ ...filters, estado: v === 'todos' ? undefined : v, page: 1 })}
      >
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Estado" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="todos">Todos</SelectItem>
          <SelectItem value="pendientes">Pendientes</SelectItem>
          <SelectItem value="atendidas">Atendidas</SelectItem>
        </SelectContent>
      </Select>

      {tieneFilrosActivos && (
        <Button variant="ghost" size="sm" onClick={limpiarFiltros}>
          <X className="mr-1 h-4 w-4" />
          Limpiar
        </Button>
      )}

    </div>
  )
}
